import { ReactNode, useMemo, useState } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  AppBar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
} from '@mui/material';
import Star from '@mui/icons-material/StarOutlined';
import StarBorder from '@mui/icons-material/StarBorderOutlined';
import Close from '@mui/icons-material/CloseOutlined';
import ChevronLeft from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRight from '@mui/icons-material/ChevronRightOutlined';
import ExpandMore from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLess from '@mui/icons-material/ExpandLessOutlined';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  CircleUser as UserIcon,
  LogOut as LogoutIcon,
  UserRound,
  ArrowUpDown,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Archive,
  ArrowLeftRight,
  CircleCheck,
  ClipboardList,
  Database,
  Handshake,
  Landmark,
  Layers,
  ShieldUser,
  Star as StarOutline,
  Tag,
  TreePine,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ModuleNavigationConfig } from '@/config/navigation.generated';
import { LegalEntitySelector } from '@/components/LegalEntitySelector';
import { MAX_FAVORITES, resolveFavoriteItems, useFavorites } from '@/hooks/useFavorites';
import { SidebarFavoritesList } from '@/components/SidebarFavoritesList';
import { useGlobalSearch } from '@/hooks/search';
import { GlobalSearchDialog } from '@/components/GlobalSearch';
import { NexoLogo, NexoIcon } from '@/components/NexoLogo';
import { SimulationToggle } from '@/components/simulation/SimulationToggle';
import { AppBreadcrumbs } from '@/components/AppBreadcrumbs';
import { ActionCenter } from '@/components/ActionCenter';
import { AiChatPanel } from '@/features/assistant/AiChatPanel';
import { HelpButton, HelpPanelDrawer } from '@/features/help/HelpPanel';
import { SimulationResultModal } from '@/components/simulation/SimulationResultModal';
import { useInboxCount } from '@/hooks/taskmanagement/useTaskInbox';
import { useNotificationsSignalR } from '@/hooks/useNotificationsSignalR';
import { useTaskInboxSignalR } from '@/hooks/taskmanagement/useTaskInboxSignalR';
import { useRecordVisitTracker } from '@/hooks/useRecordVisitTracker';
import { authService } from '@/services/auth/authService';
import { returnToAdmin } from '@/services/auth/impersonation';
import { sortModulesByLabel, useVisibleNavigation } from '@/hooks/useVisibleNavigation';
import { useTranslation } from 'react-i18next';
import { config } from '@/config/environment';
import { useHomeDashboardContext } from '@/contexts/HomeDashboardContext';

const LEFT_NAV_WIDTH = 204;
const LEFT_NAV_WIDTH_COLLAPSED = 52;
const CATEGORY_WIDTH = 220;

// Sidebar density — deliberately tighter than MUI's list defaults (48px rows,
// 24px icons), which read as an unstyled template at this nav's scale.
const NAV_ICON_SIZE = 19;
// The theme's MuiListItemText override (0.875rem) reaches the label through a
// descendant selector, which outranks a plain `sx` on the Typography itself.
// Every nav row therefore has to restate the size the same way to win.
const NAV_LABEL_FONT_SIZE = '0.8125rem';
const navRowSx = {
  borderRadius: 1,
  minHeight: 32,
  py: 0.25,
  '& .MuiListItemText-primary': { fontSize: NAV_LABEL_FONT_SIZE, fontWeight: 500 },
  '& .MuiSvgIcon-root': { fontSize: NAV_ICON_SIZE },
} as const;

// The sidebar rail draws its module icons from one outline set at one stroke
// width; the generated navigation's MUI icons mix filled and outline glyphs.
// A module missing here falls back to its generated icon.
const NAV_ICON_STROKE = 1.75;
const SIDEBAR_MODULE_ICONS: Record<string, LucideIcon> = {
  Workflow,
  Finance: Landmark,
  Sales: Handshake,
  Core: Database,
  Procurement: ClipboardList,
  Integration: ArrowLeftRight,
  Inventory: Archive,
  Pricing: Tag,
  Shared: Layers,
  Forestry: TreePine,
  Administration: ShieldUser,
  TaskManagement: CircleCheck,
};

function SidebarModuleIcon({ module }: { module: ModuleNavigationConfig }) {
  const Icon = SIDEBAR_MODULE_ICONS[module.module];
  if (!Icon) {
    const Fallback = module.icon;
    return <Fallback />;
  }
  return <Icon size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />;
}

// Link rows in the flyouts (mega menu / favorites panel). MUI stacks its own
// ListItem padding on top of ListItemText's default 4px margins, which lands
// these at ~44px per row — far too airy for what is just a column of links.
const flyoutRowSx = {
  minHeight: 28,
  py: 0.25,
  px: 0.75,
  borderRadius: 0.5,
  '& .MuiListItemText-root': { my: 0 },
  '&:hover': { bgcolor: 'action.hover' },
} as const;

// "Run as user" (impersonation) amber accent — all of it appears only while
// impersonating, which is what makes the mode recognizable at a glance.
const IMPERSONATION_COLORS = {
  amber: '#e0a54d',          // avatar fill, ring, open-chip background
  glyphOnAmber: '#46351a',   // person glyph / text on amber surfaces
  labelOnDark: '#eccb90',    // "Viewing as" label on the translucent chip
  chipOpenAvatarBg: '#faf3e6',
  chipOpenLabel: '#6d5223',
  chipOpenText: '#52401f',
  sectionBg: '#fbf5ea',      // menu impersonation section (light mode)
  sectionBorder: '#e6ddc9',
  sectionLabel: '#9a6b2e',
  dot: '#d98f2b',
  navy: '#26304a',           // "Return to admin" button
};

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(() => {
    return localStorage.getItem('nexo-sidebar-collapsed') === 'true';
  });
  const [activeModule, setActiveModule] = useState<ModuleNavigationConfig | null>(null);
  const [favoritesPanelOpen, setFavoritesPanelOpen] = useState(false);

  const { user, logout, isImpersonating } = useAuth();
  // "Run as user" (impersonation). Under D13b starting impersonation is an in-app
  // transition (context re-renders — no reload), so these must come from context, not
  // a static authService read. While impersonating, `user` IS the impersonated identity.
  const impersonatedUser = user as
    | (typeof user & { preferred_username?: string; unique_name?: string })
    | null;
  const impersonatedUserName =
    impersonatedUser?.preferred_username || impersonatedUser?.unique_name || impersonatedUser?.name || impersonatedUser?.email || '';
  const [favoritesOpen, setFavoritesOpen] = useState(() => {
    const stored = sessionStorage.getItem('nexo-favorites-open');
    return stored !== null ? stored === 'true' : true;
  });
  const displayName = user?.name || user?.email || user?.sub || 'User';
  const { t, i18n } = useTranslation();
  const { favorites, toggleFavorite, isFavorite, moveFavorite, atCap: favoritesAtCap } = useFavorites();
  const { isOpen: searchOpen, open: openSearch, close: closeSearch } = useGlobalSearch();
  const { data: inboxCount } = useInboxCount();
  // Real-time user notifications (Notifications bell) — app-global SignalR subscription.
  useNotificationsSignalR();
  // Task inbox — the badge above and the inbox page are kept current by pushes, not polling.
  useTaskInboxSignalR();
  // Feeds the command palette's history. Lives here because a record can be reached from
  // a list, global search, a deep link or the palette itself — only the route sees them all.
  useRecordVisitTracker();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const secondaryColor = muiTheme.palette.secondary.main;
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchor, setMobileMoreAnchor] = useState<null | HTMLElement>(null);
  const { setIsEditing: setHomeDashboardEditing } = useHomeDashboardContext();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    const next = !drawerCollapsed;
    setDrawerCollapsed(next);
    localStorage.setItem('nexo-sidebar-collapsed', String(next));
    // Close mega menu when collapsing
    if (!drawerCollapsed) {
      setActiveModule(null);
    }
  };

  const handleFavoritesClick = () => {
    setFavoritesPanelOpen((prev) => !prev);
    setActiveModule(null);
  };

  const handleFavoritesClose = () => {
    setFavoritesPanelOpen(false);
  };

  const handleFavoriteNavigate = (path: string) => {
    navigate(path);
    setFavoritesPanelOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleModuleClick = (module: ModuleNavigationConfig) => {
    setFavoritesPanelOpen(false);
    if (activeModule?.module === module.module) {
      setActiveModule(null);
    } else {
      setActiveModule(module);
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setActiveModule(null);
  };

  const handleCloseMenu = () => {
    setActiveModule(null);
  };

  // Modules whose backend feature is off for the tenant, and items / quick actions the user lacks
  // the permission for, are hidden; a module with no permitted items left is hidden entirely.
  const visibleModules = useVisibleNavigation();
  const sidebarModules = useMemo(
    () => sortModulesByLabel(
      visibleModules,
      (module) => t(module.labelKey || module.label, { defaultValue: module.label }),
      i18n.language,
    ),
    [visibleModules, t, i18n.language],
  );

  // The favorites in the user's order, among the items they may open.
  const favoriteItems = useMemo(
    () => resolveFavoriteItems(favorites, visibleModules),
    [favorites, visibleModules],
  );

  // Check if current path matches item path
  const isItemActive = (itemPath: string) => {
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
  };

  // Check if any item in module is active
  const isModuleActive = (module: ModuleNavigationConfig) => {
    for (const category of module.categories) {
      for (const item of category.items) {
        if (isItemActive(item.path)) {
          return true;
        }
      }
    }
    return false;
  };

  // Resolve the active module against the permission-filtered list, so the mega
  // menu only ever shows permitted categories/items/quick actions (and closes
  // if the module disappears after a permission refresh).
  const activeVisibleModule = useMemo(
    () => (activeModule ? visibleModules.find(m => m.module === activeModule.module) ?? null : null),
    [activeModule, visibleModules],
  );
  const activeCategories = activeVisibleModule?.categories ?? [];
  const megaMenuWidth = activeCategories.length * CATEGORY_WIDTH;
  const currentNavWidth = drawerCollapsed ? LEFT_NAV_WIDTH_COLLAPSED : LEFT_NAV_WIDTH;

  const userMenuOpen = Boolean(userMenuAnchor);
  // Ring/badge cutouts on the account chip avatar must match the bar behind it
  const appBarBg = isDark ? muiTheme.palette.background.default : muiTheme.palette.primary.main;

  // Impersonation section shown at the top of the account menu (desktop) and
  // the overflow menu (mobile) while "run as user" is active. No page banner.
  const impersonationMenuSection = isImpersonating && (
    <Box
      sx={{
        px: 2,
        py: 1.75,
        mb: 0.5,
        bgcolor: isDark ? alpha(IMPERSONATION_COLORS.amber, 0.12) : IMPERSONATION_COLORS.sectionBg,
        borderBottom: '1px solid',
        borderColor: isDark ? alpha(IMPERSONATION_COLORS.amber, 0.25) : IMPERSONATION_COLORS.sectionBorder,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mb: 1.5 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: IMPERSONATION_COLORS.dot, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: isDark ? IMPERSONATION_COLORS.labelOnDark : IMPERSONATION_COLORS.sectionLabel,
          }}
        >
          {t('impersonation.mode', 'Impersonation mode')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.375, mb: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            bgcolor: IMPERSONATION_COLORS.amber,
            color: IMPERSONATION_COLORS.glyphOnAmber,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <UserRound size={20} />
        </Box>
        <Box sx={{ lineHeight: 1.3, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11 }} color="text.secondary">
            {t('impersonation.youAreViewingAs', 'You are viewing as')}
          </Typography>
          <Typography noWrap sx={{ fontSize: 15, fontWeight: 700 }} color="text.primary">
            {impersonatedUserName}
          </Typography>
        </Box>
      </Box>
      <Button
        fullWidth
        onClick={() => void returnToAdmin()}
        startIcon={<ArrowLeft size={15} />}
        sx={{
          bgcolor: IMPERSONATION_COLORS.navy,
          color: '#fff',
          borderRadius: '8px',
          py: 1.25,
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': { bgcolor: '#1c2438' },
        }}
      >
        {t('impersonation.returnToAdmin', 'Return to admin')}
      </Button>
    </Box>
  );

  // Left navigation with modules
  const leftNav = (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}
      onClick={() => {
        // Close mega menu when clicking anywhere in the sidebar
        if (activeModule) handleCloseMenu();
      }}
    >
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '58px !important',
        px: drawerCollapsed ? 0.5 : 1.5,
      }}>
        <Box
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { opacity: 0.8 } }}
        >
          {drawerCollapsed ? (
            <NexoIcon sx={{ fontSize: 26 }} />
          ) : (
            // 20px of letterform ≈ 98px wide at the wordmark's 4.9:1 ratio — the same
            // footprint the old icon+text lockup had in the 204px rail.
            <NexoLogo height={20} />
          )}
        </Box>
      </Toolbar>

      <Divider />

      {/* Favorites Section - collapsed: show icon with popover, expanded: show full list */}
      {favoriteItems.length > 0 && (
        <>
          {drawerCollapsed ? (
            // Collapsed: Show favorites icon that opens panel
            <List disablePadding sx={{ px: 0.75, py: 0.5 }}>
              <ListItem disablePadding sx={{ py: 0 }}>
                <ListItemButton
                  onClick={(e) => { e.stopPropagation(); handleFavoritesClick(); }}
                  sx={{
                    ...navRowSx,
                    justifyContent: 'center',
                    px: 0.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 0 }}>
                    <StarOutline size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            </List>
          ) : (
            // Expanded: Show full favorites list with collapsible toggle
            <>
              <List disablePadding sx={{ px: 0.75, py: 0.5 }}>
                <ListItem disablePadding sx={{ py: 0 }}>
                  <ListItemButton
                    onClick={() => {
                      const next = !favoritesOpen;
                      setFavoritesOpen(next);
                      sessionStorage.setItem('nexo-favorites-open', String(next));
                    }}
                    sx={{ ...navRowSx, px: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 28, mr: 0 }}>
                      <StarOutline size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.favorites')} />
                    {favoritesOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
              </List>
              <Collapse in={favoritesOpen} timeout="auto" unmountOnExit>
                <SidebarFavoritesList
                  items={favoriteItems}
                  isActive={isItemActive}
                  onNavigate={navigate}
                  onMove={moveFavorite}
                  labelFontSize={NAV_LABEL_FONT_SIZE}
                />
              </Collapse>
            </>
          )}
          <Divider />
        </>
      )}

      {/* Module List */}
      <List sx={{ flexGrow: 1, px: 0.75, py: 0.5, overflow: 'auto' }}>
        {sidebarModules.map((module) => {
          const isActive = isModuleActive(module);
          const isSelected = activeModule?.module === module.module;
          const taskCount = module.module === 'TaskManagement' ? inboxCount?.myTasksCount ?? 0 : 0;
          const taskOverdue = (inboxCount?.overdueCount ?? 0) > 0;

          return (
            <ListItem key={module.module} disablePadding sx={{ py: 0.125 }}>
              <ListItemButton
                onClick={(e) => { e.stopPropagation(); handleModuleClick(module); }}
                selected={isActive || isSelected}
                sx={{
                  ...navRowSx,
                  justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                  px: drawerCollapsed ? 0.5 : 1,
                  bgcolor: isSelected ? alpha(secondaryColor, 0.10) : undefined,
                  color: isSelected ? secondaryColor : undefined,
                  '&:hover': {
                    bgcolor: isSelected ? alpha(secondaryColor, 0.15) : undefined,
                  },
                  '& .MuiListItemIcon-root': {
                    color: (isSelected || isActive) ? secondaryColor : undefined,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: drawerCollapsed ? 'auto' : 28, mr: 0 }}>
                  {/* Collapsed there is no label to sit beside, so the count falls back to
                      an overlay badge; expanded it rides at the end of the row instead. */}
                  {taskCount > 0 && drawerCollapsed ? (
                    <Badge
                      badgeContent={taskCount}
                      color={taskOverdue ? 'error' : 'primary'}
                      max={99}
                      sx={{ '& .MuiBadge-badge': { fontSize: '0.625rem', height: 15, minWidth: 15 } }}
                    >
                      <SidebarModuleIcon module={module} />
                    </Badge>
                  ) : (
                    <SidebarModuleIcon module={module} />
                  )}
                </ListItemIcon>
                {!drawerCollapsed && (
                  <>
                    <ListItemText primary={t(module.labelKey || module.label, { defaultValue: module.label })} sx={{ minWidth: 0 }} />
                    {taskCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          ml: 0.75,
                          minWidth: 18,
                          height: 17,
                          px: 0.5,
                          borderRadius: 999,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          lineHeight: 1,
                          bgcolor: taskOverdue ? 'error.main' : 'primary.main',
                          color: taskOverdue ? 'error.contrastText' : 'primary.contrastText',
                        }}
                      >
                        {taskCount > 99 ? '99+' : taskCount}
                      </Box>
                    )}
                  </>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Version + Collapse Button */}
      <Divider />
      <Box sx={{ px: 0.75, py: 0.25, display: 'flex', alignItems: 'center', justifyContent: drawerCollapsed ? 'center' : 'space-between' }}>
        {!drawerCollapsed && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ pl: 1, fontSize: '0.6875rem', userSelect: 'text' }}
          >
            {config.appVersion}
          </Typography>
        )}
        <IconButton
          onClick={handleDrawerCollapse}
          size="small"
          sx={{ color: 'text.secondary', p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 } }}
        >
          {drawerCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );

  // Acumatica-style mega menu
  const megaMenu = activeVisibleModule && (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: 60,
        left: currentNavWidth,
        width: megaMenuWidth,
        maxWidth: `calc(100vw - ${currentNavWidth}px - 48px)`,
        height: 'calc(100vh - 58px)',
        // Above the drawer layer (theme.zIndex.drawer = 1200): the related-view slide-out panels
        // are persistent Drawers at 1200 and sit later in the DOM, so an equal z-index puts the
        // open menu UNDER them. Transient navigation chrome must cover page panels; 1250 stays
        // below real modals (1300) and snackbars.
        zIndex: 1250,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 0.875,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ fontSize: '1rem' }}>
          {t(activeVisibleModule.labelKey || activeVisibleModule.label, { defaultValue: activeVisibleModule.label })}
        </Typography>
        <IconButton size="small" onClick={handleCloseMenu} sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
          <Close />
        </IconButton>
      </Box>

      {/* Quick Actions */}
      {activeVisibleModule.quickActions.length > 0 && (
        <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {activeVisibleModule.quickActions.map((action) => {
            const ActionIcon = action.icon;
            return (
              <Chip
                key={action.id}
                size="small"
                icon={<ActionIcon />}
                label={action.labelKey ? t(action.labelKey, { defaultValue: action.label }) : action.label}
                onClick={() => action.path && handleNavigate(action.path)}
                color="primary"
                variant="filled"
                sx={{ fontWeight: 500, '& .MuiChip-icon': { fontSize: 15 } }}
              />
            );
          })}
        </Box>
      )}

      {/* Categories Grid */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          p: 1.5,
          gap: 1.5,
        }}
      >
        {activeCategories.map((category) => {
          const CategoryIcon = category.icon;

          return (
            <Box
              key={category.id}
              sx={{
                width: CATEGORY_WIDTH - 16,
                flexShrink: 0,
              }}
            >
              {/* Category Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 0.75,
                  pb: 0.625,
                  borderBottom: '2px solid',
                  borderColor: isDark ? secondaryColor : 'primary.main',
                }}
              >
                <CategoryIcon sx={{ fontSize: 17, color: isDark ? secondaryColor : 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={600} color={isDark ? secondaryColor : 'primary.main'}>
                  {t(category.labelKey || category.label, { defaultValue: category.label })}
                </Typography>
              </Box>

              {/* Category Items */}
              <List dense disablePadding>
                {category.items.map((item) => {
                  const itemIsFavorite = isFavorite(item.id);
                  // At the cap a new star is refused by the server, so it is not offered.
                  const starRefused = !itemIsFavorite && favoritesAtCap;

                  return (
                    <ListItem
                      key={item.id}
                      disablePadding
                      // MUI reserves a fixed 48px right gutter for secondaryAction; the star
                      // button is only ~20px, and the extra width forces needless label wrapping.
                      sx={{ py: 0.125, pr: 3.5 }}
                      secondaryAction={
                        <Tooltip title={starRefused ? t('nav.favoritesAtCap', { max: MAX_FAVORITES }) : ''}>
                          {/* A disabled button swallows pointer events, so the tooltip needs a wrapper to hang on. */}
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              disabled={starRefused}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(item.id);
                              }}
                              sx={{
                                opacity: itemIsFavorite ? 1 : 0,
                                transition: 'opacity 0.2s',
                                '.MuiListItem-root:hover &': { opacity: 1 },
                                p: 0.25,
                              }}
                            >
                              {itemIsFavorite ? (
                                <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                              ) : (
                                <StarBorder sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemButton
                        onClick={() => handleNavigate(item.path)}
                        selected={isItemActive(item.path)}
                        dense
                        sx={flyoutRowSx}
                      >
                        <ListItemText
                          primary={t(item.labelKey || item.label, { defaultValue: item.label })}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: isDark ? 'text.primary' : 'primary.main',
                            sx: {
                              fontWeight: isItemActive(item.path) ? 600 : 400,
                              '&:hover': { textDecoration: 'underline', color: isDark ? secondaryColor : undefined },
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );

  // Click outside handler overlay
  const menuOverlay = activeVisibleModule && (
    <Box
      sx={{
        position: 'fixed',
        top: 58,
        left: currentNavWidth + megaMenuWidth,
        right: 0,
        bottom: 0,
        zIndex: 1249, // just under the menu (1250), above the drawer-level panels it must catch clicks over
      }}
      onClick={handleCloseMenu}
    />
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentNavWidth}px)` },
          ml: { md: `${currentNavWidth}px` },
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar sx={{ minHeight: '58px !important', pr: '12px !important' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon size={20} />
          </IconButton>
          <AppBreadcrumbs />
          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop: show all icons inline */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            <SimulationToggle />
            <IconButton
              color="inherit"
              onClick={openSearch}
              title={t('nav.searchTooltip', { shortcut: navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K' })}
            >
              <SearchIcon size={20} />
            </IconButton>
            <Box sx={{ mx: 0.5 }}><LegalEntitySelector /></Box>
            <ActionCenter />
            <HelpButton />
            <AiChatPanel />
            {isImpersonating ? (
              // Account chip — the icon itself signals impersonation (amber
              // ring + swap badge + caret), replacing the old page banner.
              <ButtonBase
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                aria-haspopup="menu"
                title={t('nav.account', 'Account')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: 999,
                  pl: 0.5,
                  pr: 1.25,
                  py: 0.5,
                  textAlign: 'left',
                  bgcolor: userMenuOpen ? IMPERSONATION_COLORS.amber : 'rgba(255,255,255,0.12)',
                  border: '1px solid',
                  borderColor: userMenuOpen ? IMPERSONATION_COLORS.amber : alpha(IMPERSONATION_COLORS.amber, 0.6),
                }}
              >
                <Box sx={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: userMenuOpen ? IMPERSONATION_COLORS.chipOpenAvatarBg : IMPERSONATION_COLORS.amber,
                      color: userMenuOpen ? IMPERSONATION_COLORS.chipOpenLabel : IMPERSONATION_COLORS.glyphOnAmber,
                      boxShadow: userMenuOpen
                        ? 'none'
                        : `0 0 0 2px ${IMPERSONATION_COLORS.amber}, 0 0 0 4px ${appBarBg}`,
                    }}
                  >
                    <UserRound size={16} />
                  </Box>
                  {!userMenuOpen && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: IMPERSONATION_COLORS.amber,
                        border: `2px solid ${appBarBg}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ArrowUpDown size={7} strokeWidth={3} color={IMPERSONATION_COLORS.glyphOnAmber} />
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 9,
                      fontWeight: userMenuOpen ? 700 : 600,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      lineHeight: 1.1,
                      color: userMenuOpen ? IMPERSONATION_COLORS.chipOpenLabel : IMPERSONATION_COLORS.labelOnDark,
                    }}
                  >
                    {t('impersonation.viewingAsLabel', 'Viewing as')}
                  </Typography>
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      fontSize: 13,
                      fontWeight: userMenuOpen ? 700 : 600,
                      lineHeight: 1.1,
                      color: userMenuOpen ? IMPERSONATION_COLORS.chipOpenText : '#fff',
                    }}
                  >
                    {impersonatedUserName}
                  </Typography>
                </Box>
                {userMenuOpen ? (
                  <ChevronUp size={14} color={IMPERSONATION_COLORS.chipOpenLabel} style={{ marginLeft: 2, flexShrink: 0 }} />
                ) : (
                  <ChevronDown size={14} color="#fff" style={{ marginLeft: 2, flexShrink: 0 }} />
                )}
              </ButtonBase>
            ) : (
              <IconButton color="inherit" onClick={(e) => setUserMenuAnchor(e.currentTarget)} title={t('nav.account', 'Account')}>
                <UserIcon size={20} />
              </IconButton>
            )}
            <Menu
              anchorEl={userMenuAnchor}
              open={userMenuOpen}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={isImpersonating ? { sx: { pt: 0 } } : undefined}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1.5,
                    minWidth: isImpersonating ? 320 : 240,
                    ...(isImpersonating && { borderRadius: '12px', overflow: 'hidden' }),
                  },
                },
              }}
            >
              {impersonationMenuSection}
              {setHomeDashboardEditing && (
                <MenuItem onClick={() => { setHomeDashboardEditing(true); setUserMenuAnchor(null); }}>
                  <ListItemIcon><DashboardCustomizeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('dashboard.customizeDashboard', 'Customize Dashboard')}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => { navigate('/user/settings'); setUserMenuAnchor(null); }}>
                <ListItemIcon><MuiSettingsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('nav.userSettings', 'Settings')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleLogout(); setUserMenuAnchor(null); }}>
                <ListItemIcon><LogoutIcon size={18} /></ListItemIcon>
                <ListItemText>{t('common.logout', 'Logout')}</ListItemText>
              </MenuItem>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" noWrap>
                  {displayName}
                </Typography>
                {user?.email && user.email !== displayName && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {user.email}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {t('nav.tenant', 'Tenant')}: {authService.getTenantName() || '—'}
                </Typography>
              </Box>
            </Menu>
          </Box>

          {/* Small screens: compact icon row + overflow menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
            <IconButton color="inherit" onClick={openSearch} size="small">
              <SearchIcon size={18} />
            </IconButton>
            <LegalEntitySelector />
            <ActionCenter />
            <HelpButton />
            <AiChatPanel />
            <IconButton
              color="inherit"
              onClick={(e) => setMobileMoreAnchor(e.currentTarget)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMoreAnchor}
              open={Boolean(mobileMoreAnchor)}
              onClose={() => setMobileMoreAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={isImpersonating ? { sx: { pt: 0 } } : undefined}
              slotProps={{ paper: { sx: { mt: 1, minWidth: isImpersonating ? 280 : 200 } } }}
            >
              {impersonationMenuSection}
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.disabled" noWrap display="block">
                  {t('nav.tenant', 'Tenant')}: {authService.getTenantName() || '—'}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { setMobileMoreAnchor(null); }}>
                <SimulationToggle />
              </MenuItem>
              {setHomeDashboardEditing && (
                <MenuItem onClick={() => { setHomeDashboardEditing(true); setMobileMoreAnchor(null); }}>
                  <ListItemIcon><DashboardCustomizeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('dashboard.customizeDashboard', 'Customize Dashboard')}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => { navigate('/user/settings'); setMobileMoreAnchor(null); }}>
                <ListItemIcon><MuiSettingsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('nav.userSettings', 'Settings')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleLogout(); setMobileMoreAnchor(null); }}>
                <ListItemIcon><LogoutIcon size={18} /></ListItemIcon>
                <ListItemText>{t('common.logout', 'Logout')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: LEFT_NAV_WIDTH },
        }}
      >
        {leftNav}
      </Drawer>

      {/* Desktop Left Navigation */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: currentNavWidth,
            borderRight: '1px solid',
            borderColor: 'divider',
            transition: 'width 0.2s',
          },
        }}
        open
      >
        {leftNav}
      </Drawer>

      {/* Mega Menu */}
      {megaMenu}
      {menuOverlay}

      {/* Favorites Panel (same style as mega menu) */}
      {favoritesPanelOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: 60,
            left: currentNavWidth,
            width: CATEGORY_WIDTH,
            height: 'calc(100vh - 58px)',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 1.5,
              py: 0.875,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ fontSize: '1rem' }}>
              {t('nav.favorites')}
            </Typography>
            <IconButton size="small" onClick={handleFavoritesClose} sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
              <Close />
            </IconButton>
          </Box>

          {/* Favorite Items */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5 }}>
            <List dense disablePadding>
              {favoriteItems.map(({ item }) => (
                <ListItem key={`fav-panel-${item.id}`} disablePadding sx={{ py: 0.125 }}>
                  <ListItemButton
                    onClick={() => handleFavoriteNavigate(item.path)}
                    selected={isItemActive(item.path)}
                    dense
                    sx={flyoutRowSx}
                  >
                    <ListItemText
                      primary={t(item.labelKey || item.label, { defaultValue: item.label })}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: isDark ? 'text.primary' : 'primary.main',
                        sx: {
                          fontWeight: isItemActive(item.path) ? 600 : 400,
                          '&:hover': { textDecoration: 'underline', color: isDark ? secondaryColor : undefined },
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>
      )}
      {/* Favorites overlay (click outside to close) */}
      {favoritesPanelOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 58,
            left: currentNavWidth + CATEGORY_WIDTH,
            right: 0,
            bottom: 0,
            zIndex: 1199,
          }}
          onClick={handleFavoritesClose}
        />
      )}

      {/* Global Search Dialog */}
      <GlobalSearchDialog open={searchOpen} onClose={closeSearch} />
      <HelpPanelDrawer />

      {/* Simulation Result Modal */}
      <SimulationResultModal />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${currentNavWidth}px)` },
          ml: { md: `${currentNavWidth}px` },
          mt: 8,
          height: 'calc(100vh - 64px)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
